
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import hostlink from "../Hostlink/hostlink";
import "./TailorReport.css";

const TailorReport = () => {

    // =====================================================
    // TODAY
    // =====================================================

    const today = new Date().toISOString().split("T")[0];


    // =====================================================
    // SHOP ID
    // =====================================================

    const shopid = localStorage.getItem("MYPCKARTUSER")
        ? JSON.parse(
            localStorage.getItem("MYPCKARTUSER")
        )[0].id
        : "0";


    // =====================================================
    // STATE
    // =====================================================

    const [tailors, setTailors] = useState([]);

    const [reportData, setReportData] = useState([]);

    const [fromDate, setFromDate] = useState("2024-01-01");

    const [toDate, setToDate] = useState(today);

    const [tailorCode, setTailorCode] = useState("0");

    const [status, setStatus] = useState("ALL");

    const [search, setSearch] = useState("");

    const [loading, setLoading] = useState(false);


    // =====================================================
    // FETCH TAILORS
    // =====================================================

    const fetchTailors = async () => {

        try {

            const response = await axios.get(
                `${hostlink}/${shopid}/TailorMasCont`
            );

            console.log(
                "Tailors:",
                response.data
            );

            setTailors(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Tailor fetch error:",
                error
            );

            toast.error(
                error.response?.data ||
                "Unable to load tailor list"
            );
        }
    };


    // =====================================================
    // FETCH REPORT
    // =====================================================

    const fetchReport = async () => {

        try {

            setLoading(true);

            const response = await axios.get(
                `${hostlink}/api/tailor/report`,
                {
                    params: {
                        shopid: shopid,
                        fromdate: fromDate,
                        todate: toDate,
                        tailorcode: tailorCode
                    }
                }
            );

            console.log(
                "Tailor Report:",
                response.data
            );

            setReportData(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Report fetch error:",
                error
            );

            toast.error(
                error.response?.data ||
                "Unable to load tailor report"
            );

            setReportData([]);

        } finally {

            setLoading(false);
        }
    };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        if (Number(shopid) !== 0) {

            fetchTailors();

            fetchReport();
        }

    }, [shopid]);


    // =====================================================
    // GENERATE REPORT
    // =====================================================

    const generateReport = () => {

        if (!fromDate) {

            toast.error(
                "Please select From Date"
            );

            return;
        }

        if (!toDate) {

            toast.error(
                "Please select To Date"
            );

            return;
        }

        if (fromDate > toDate) {

            toast.error(
                "From Date cannot be greater than To Date"
            );

            return;
        }

        fetchReport();
    };


    // =====================================================
    // GET STATUS
    // =====================================================

    const getStatus = (row) => {

        const issued =
            Number(
                row.issuedpcs ??
                row.totalpcs ??
                row.totpcs ??
                0
            );

        const received =
            Number(
                row.receivedpcs ??
                row.receiveqty ??
                0
            );

        const pending =
            Math.max(
                0,
                issued - received
            );


        if (pending <= 0 && issued > 0) {

            return "FINAL RECEIVED";
        }

        if (received > 0 && pending > 0) {

            return "PARTIALLY RECEIVED";
        }

        return "PENDING";
    };


    // =====================================================
    // FILTER DATA
    // =====================================================

    const filteredData = useMemo(() => {

        const text =
            search
                .toLowerCase()
                .trim();


        return reportData.filter(row => {

            // ---------------------------------------------
            // STATUS
            // ---------------------------------------------

            const rowStatus =
                getStatus(row);


            let statusMatch = true;


            if (status === "PENDING") {

                statusMatch =
                    rowStatus === "PENDING";
            }


            if (status === "PARTIAL") {

                statusMatch =
                    rowStatus === "PARTIALLY RECEIVED";
            }


            if (status === "FINAL") {

                statusMatch =
                    rowStatus === "FINAL RECEIVED";
            }


            // ---------------------------------------------
            // SEARCH
            // ---------------------------------------------

            const searchMatch =
                !text ||

                String(
                    row.tailorname || ""
                )
                    .toLowerCase()
                    .includes(text)

                ||

                String(
                    row.billno || ""
                )
                    .toLowerCase()
                    .includes(text)

                ||

                String(
                    row.style || ""
                )
                    .toLowerCase()
                    .includes(text)

                ||

                String(
                    row.size || ""
                )
                    .toLowerCase()
                    .includes(text);


            return (
                statusMatch &&
                searchMatch
            );

        });

    }, [
        reportData,
        search,
        status
    ]);


    // =====================================================
    // CALCULATIONS
    // =====================================================

    const totals = useMemo(() => {

        let issued = 0;
        let received = 0;
        let pending = 0;
        let amount = 0;


        filteredData.forEach(row => {

            const issuedpcs =
                Number(
                    row.issuedpcs ??
                    row.totalpcs ??
                    row.totpcs ??
                    0
                );


            const receivedpcs =
                Number(
                    row.receivedpcs ??
                    row.receiveqty ??
                    0
                );


            const pendingpcs =
                Math.max(
                    0,
                    issuedpcs - receivedpcs
                );


            const rate =
                Number(
                    row.rate || 0
                );


            issued += issuedpcs;

            received += receivedpcs;

            pending += pendingpcs;

            amount += receivedpcs * rate;

        });


        return {
            issued,
            received,
            pending,
            amount
        };

    }, [filteredData]);


    // =====================================================
    // TAILOR SUMMARY
    // =====================================================

    const tailorSummary = useMemo(() => {

        const map = {};


        filteredData.forEach(row => {

            const code =
                row.tailorcode ??
                row.tailorCode ??
                0;


            const name =
                row.tailorname ||
                row.tailorName ||
                "Unknown";


            const key =
                `${code}-${name}`;


            if (!map[key]) {

                map[key] = {

                    tailorcode: code,

                    tailorname: name,

                    issued: 0,

                    received: 0,

                    pending: 0,

                    amount: 0
                };
            }


            const issued =
                Number(
                    row.issuedpcs ??
                    row.totalpcs ??
                    row.totpcs ??
                    0
                );


            const received =
                Number(
                    row.receivedpcs ??
                    row.receiveqty ??
                    0
                );


            const pending =
                Math.max(
                    0,
                    issued - received
                );


            const rate =
                Number(
                    row.rate || 0
                );


            map[key].issued += issued;

            map[key].received += received;

            map[key].pending += pending;

            map[key].amount +=
                received * rate;

        });


        return Object.values(map);

    }, [filteredData]);


    // =====================================================
    // EXPORT EXCEL
    // =====================================================

    const exportExcel = () => {

        const params = new URLSearchParams({

            shopid: shopid,

            fromdate: fromDate,

            todate: toDate,

            tailorcode: tailorCode,

            status: status

        });


        window.open(
            `${hostlink}/api/tailor/report/excel?${params.toString()}`,
            "_blank"
        );
    };


    // =====================================================
    // EXPORT PDF
    // =====================================================

    const exportPdf = () => {

        const params = new URLSearchParams({

            shopid: shopid,

            fromdate: fromDate,

            todate: toDate,

            tailorcode: tailorCode,

            status: status

        });


        window.open(
            `${hostlink}/api/tailor/report/pdf?${params.toString()}`,
            "_blank"
        );
    };


    // =====================================================
    // RESET
    // =====================================================

    const resetReport = () => {

        setFromDate(today);

        setToDate(today);

        setTailorCode("0");

        setStatus("ALL");

        setSearch("");

        setTimeout(() => {

            fetchReport();

        }, 0);
    };


    // =====================================================
    // UI
    // =====================================================

    return (

        <div className="tailor-report-container">

            <ToastContainer />


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="report-header">

                <div>

                    <h2>
                        Tailor Report
                    </h2>

                    <p>
                        Tailor-wise pending and received work
                    </p>

                </div>

            </div>


            {/* =================================================
                FILTER CARD
            ================================================= */}

            <div className="report-card">

                <div className="report-filters">


                    {/* FROM DATE */}

                    <div className="filter-group">

                        <label>
                            From Date
                        </label>

                        <input
                            type="date"
                            value={fromDate}
                            onChange={
                                e =>
                                    setFromDate(
                                        e.target.value
                                    )
                            }
                        />

                    </div>


                    {/* TO DATE */}

                    <div className="filter-group">

                        <label>
                            To Date
                        </label>

                        <input
                            type="date"
                            value={toDate}
                            onChange={
                                e =>
                                    setToDate(
                                        e.target.value
                                    )
                            }
                        />

                    </div>


                    {/* TAILOR */}

                    <div className="filter-group">

                        <label>
                            Tailor
                        </label>

                        <select
                            value={tailorCode}
                            onChange={
                                e =>
                                    setTailorCode(
                                        e.target.value
                                    )
                            }
                        >

                            <option value="0">
                                All Tailors
                            </option>


                            {tailors.map(
                                (tailor, index) => (

                                    <option
                                        key={
                                            tailor.tailorcode ??
                                            tailor.id ??
                                            index
                                        }
                                        value={
                                            tailor.tailorcode ??
                                            tailor.id ??
                                            tailor.code
                                        }
                                    >

                                        {tailor.tailorname ||
                                            tailor.tname ||
                                            tailor.name}

                                    </option>

                                )
                            )}

                        </select>

                    </div>


                    {/* STATUS */}

                    <div className="filter-group">

                        <label>
                            Status
                        </label>

                        <select
                            value={status}
                            onChange={
                                e =>
                                    setStatus(
                                        e.target.value
                                    )
                            }
                        >

                            <option value="ALL">
                                All
                            </option>

                            <option value="PENDING">
                                Pending
                            </option>

                            <option value="PARTIAL">
                                Partially Received
                            </option>

                            <option value="FINAL">
                                Final Received
                            </option>

                        </select>

                    </div>


                    {/* SEARCH */}

                    <div className="filter-group search-group">

                        <label>
                            Search
                        </label>

                        <input
                            type="text"
                            placeholder="Tailor / Lot / Style / Size..."
                            value={search}
                            onChange={
                                e =>
                                    setSearch(
                                        e.target.value
                                    )
                            }
                        />

                    </div>


                </div>


                {/* BUTTONS */}

                <div className="report-actions">

                    <button
                        className="generate-btn"
                        onClick={generateReport}
                        disabled={loading}
                    >

                        {loading
                            ? "Loading..."
                            : "Generate Report"}

                    </button>


                    <button
                        className="reset-btn"
                        onClick={resetReport}
                    >
                        Reset
                    </button>


                    <button
                        className="excel-btn"
                        onClick={exportExcel}
                    >
                        Export Excel
                    </button>


                    <button
                        className="pdf-btn"
                        onClick={exportPdf}
                    >
                        Export PDF
                    </button>

                </div>

            </div>


            {/* =================================================
                SUMMARY BOXES
            ================================================= */}

            <div className="summary-grid">

                <div className="summary-card">

                    <span>
                        Issued Pcs
                    </span>

                    <strong>
                        {totals.issued.toFixed(2)}
                    </strong>

                </div>


                <div className="summary-card">

                    <span>
                        Received Pcs
                    </span>

                    <strong>
                        {totals.received.toFixed(2)}
                    </strong>

                </div>


                <div className="summary-card pending-summary">

                    <span>
                        Pending Pcs
                    </span>

                    <strong>
                        {totals.pending.toFixed(2)}
                    </strong>

                </div>


                <div className="summary-card amount-summary">

                    <span>
                        Received Amount
                    </span>

                    <strong>
                        ₹ {totals.amount.toFixed(2)}
                    </strong>

                </div>

            </div>


            {/* =================================================
                TAILOR SUMMARY
            ================================================= */}

            <div className="report-card">

                <div className="card-title">

                    <div>

                        <h4>
                            Tailor Wise Summary
                        </h4>

                        <span>
                            {tailorSummary.length} Tailors
                        </span>

                    </div>

                </div>


                <div className="table-wrapper">

                    <table>

                        <thead>

                            <tr>

                                <th>
                                    #
                                </th>

                                <th>
                                    Tailor
                                </th>

                                <th>
                                    Issued Pcs
                                </th>

                                <th>
                                    Received Pcs
                                </th>

                                <th>
                                    Pending Pcs
                                </th>

                                <th>
                                    Amount
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {tailorSummary.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="6"
                                        className="empty"
                                    >
                                        No data found
                                    </td>

                                </tr>

                            ) : (

                                tailorSummary.map(
                                    (row, index) => (

                                        <tr
                                            key={
                                                `${row.tailorcode}-${index}`
                                            }
                                        >

                                            <td>
                                                {index + 1}
                                            </td>

                                            <td>

                                                <strong>
                                                    {row.tailorname}
                                                </strong>

                                            </td>

                                            <td>
                                                {row.issued.toFixed(2)}
                                            </td>

                                            <td>
                                                {row.received.toFixed(2)}
                                            </td>

                                            <td>

                                                <span
                                                    className={
                                                        row.pending > 0
                                                            ? "status-pending"
                                                            : "status-final"
                                                    }
                                                >
                                                    {row.pending.toFixed(2)}
                                                </span>

                                            </td>

                                            <td>

                                                <strong>
                                                    ₹{" "}
                                                    {row.amount.toFixed(2)}
                                                </strong>

                                            </td>

                                        </tr>

                                    )
                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* =================================================
                DETAIL REPORT
            ================================================= */}

            <div className="report-card">

                <div className="card-title">

                    <div>

                        <h4>
                            Detailed Report
                        </h4>

                        <span>
                            {filteredData.length} Records
                        </span>

                    </div>

                </div>


                <div className="table-wrapper">

                    <table>

                        <thead>

                            <tr>

                                <th>
                                    #
                                </th>

                                <th>
                                    Tailor
                                </th>

                                <th>
                                    Lot No
                                </th>

                                <th>
                                    Style
                                </th>

                                <th>
                                    Size
                                </th>

                                <th>
                                    Issued Pcs
                                </th>

                                <th>
                                    Received Pcs
                                </th>

                                <th>
                                    Pending Pcs
                                </th>

                                <th>
                                    Rate
                                </th>

                                <th>
                                    Amount
                                </th>

                                <th>
                                    Status
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="11"
                                        className="empty"
                                    >
                                        Loading report...
                                    </td>

                                </tr>

                            ) : filteredData.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="11"
                                        className="empty"
                                    >
                                        No report data found
                                    </td>

                                </tr>

                            ) : (

                                filteredData.map(
                                    (row, index) => {

                                        const issued =
                                            Number(
                                                row.issuedpcs ??
                                                row.totalpcs ??
                                                row.totpcs ??
                                                0
                                            );


                                        const received =
                                            Number(
                                                row.receivedpcs ??
                                                row.receiveqty ??
                                                0
                                            );


                                        const pending =
                                            Math.max(
                                                0,
                                                issued - received
                                            );


                                        const rate =
                                            Number(
                                                row.rate || 0
                                            );


                                        const amount =
                                            received * rate;


                                        const rowStatus =
                                            getStatus(row);


                                        return (

                                            <tr
                                                key={
                                                    row.purchaseId ??
                                                    row.id ??
                                                    index
                                                }
                                            >

                                                <td>
                                                    {index + 1}
                                                </td>


                                                <td>

                                                    <span className="tailor-name">

                                                        {row.tailorname ||
                                                            row.tailorName ||
                                                            "-"}

                                                    </span>

                                                </td>


                                                <td>

                                                    <strong>
                                                        {row.billno ||
                                                            "-"}
                                                    </strong>

                                                </td>


                                                <td>
                                                    {row.style ||
                                                        "-"}
                                                </td>


                                                <td>
                                                    {row.size ||
                                                        "-"}
                                                </td>


                                                <td>
                                                    {issued.toFixed(2)}
                                                </td>


                                                <td>
                                                    {received.toFixed(2)}
                                                </td>


                                                <td>

                                                    <span
                                                        className={
                                                            pending > 0
                                                                ? "status-pending"
                                                                : "status-final"
                                                        }
                                                    >
                                                        {pending.toFixed(2)}
                                                    </span>

                                                </td>


                                                <td>
                                                    ₹ {rate.toFixed(2)}
                                                </td>


                                                <td>

                                                    <strong>
                                                        ₹{" "}
                                                        {amount.toFixed(2)}
                                                    </strong>

                                                </td>


                                                <td>

                                                    <span
                                                        className={
                                                            rowStatus ===
                                                            "FINAL RECEIVED"
                                                                ? "badge-final"
                                                                : rowStatus ===
                                                                  "PARTIALLY RECEIVED"
                                                                    ? "badge-partial"
                                                                    : "badge-pending"
                                                        }
                                                    >
                                                        {rowStatus}
                                                    </span>

                                                </td>

                                            </tr>

                                        );

                                    }
                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>
    );
};

export default TailorReport;

